import React, { useState, useRef} from "react";
import {
  Activity,
  Bot,
  Loader,
  X,
  Plus,
  Calendar,
  AlertTriangle,
  Save,
  Pencil,
  Trash2,
  Eye,
} from "lucide-react";
import { useMarcadores } from "../context/MarcadoresContext";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useAuth } from "../../auth/context/AuthContext";
import {
  podeEscreverClinico,
  ehAdministrador,
  ehMedico,
} from "../../../shared/utils/permissoesCategoria";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import Button from "../../../shared/components/ui/Button";
import CampoAutoanticorpo from "./CampoAutoanticorpo";
import {
  TIPO_AUTOANTICORPO,
  padronizarRegistroMarcadores,
  camposNaoReconhecidos,
} from "../../sorologias/services/normalizarSorologia";
import { gerarPromptMarcadores } from "../../ia-analise/services/gerarPromptMarcadores";
import { callAnalyze } from "../../../lib/analyzeClient";
import { ehDataFutura, msgDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import { useExames } from "../../exames/context/ExamesContext";
import TextoAnaliseMarkdown from "../../../shared/components/TextoAnaliseMarkdown";

// ========================================
// 🎨 FUNÇÕES DE ALERTA PARA AFP (CHC)
// ========================================

// Função para determinar cor da célula de AFP
const getCorAFP = (
  valor: string | undefined
): { bg: string; text: string; border: string } => {
  if (!valor)
    return { bg: "transparent", text: "inherit", border: "transparent" };

  const num = parseFloat(valor);
  if (isNaN(num))
    return { bg: "transparent", text: "inherit", border: "transparent" };

  if (num >= 1000) {
    // 🔴 DROP-OUT - Vermelho intenso
    return { bg: "#fef2f2", text: "#dc2626", border: "#dc2626" };
  } else if (num > 400) {
    // 🟠 ALTO RISCO - Laranja
    return { bg: "#fff7ed", text: "#ea580c", border: "#ea580c" };
  } else if (num > 200) {
    // 🟡 RISCO MODERADO - Amarelo (critério Barcelona)
    return { bg: "#fefce8", text: "#ca8a04", border: "#ca8a04" };
  } else if (num > 10) {
    // 🟡 ELEVADO - Amarelo claro
    return { bg: "#fffbeb", text: "#b45309", border: "#f59e0b" };
  }
  // 🟢 NORMAL
  return { bg: "#f0fdf4", text: "#16a34a", border: "#16a34a" };
};

// Marcadores tumorais DINÂMICOS (tendência importa) — recebem o tratamento da
// AFP (cor graduada + seta + alerta de duplicação). B195: relevantes na
// hepatobiliopancreática (CA 19-9 colangio/pâncreas, CEA, CA 125).
const MARCADORES_TUMORAIS = ["afp", "ca199", "cea", "ca125"];

// LSR (limite superior de referência) de CA 19-9 / CEA / CA 125 (B195).
// A AFP NÃO entra aqui — usa getCorAFP, com tiers absolutos de CHC.
const LSR_TUMORAL: Record<string, number> = { ca199: 37, cea: 5, ca125: 35 };

// Cor graduada por MÚLTIPLOS do LSR para CA 19-9 / CEA / CA 125 (B195):
// ≤1× verde | 1–3× amarelo (leve) | 3–10× laranja (moderado) | >10× vermelho.
const getCorTumoral = (
  chave: string,
  valor: string | undefined
): { bg: string; text: string; border: string } => {
  const transparente = {
    bg: "transparent",
    text: "inherit",
    border: "transparent",
  };
  const lsr = LSR_TUMORAL[chave];
  if (!lsr || valor === undefined || valor === null || valor === "")
    return transparente;
  const num = parseFloat(String(valor).replace(",", "."));
  if (isNaN(num)) return transparente;
  const ratio = num / lsr;
  if (ratio <= 1)
    return { bg: "#f0fdf4", text: "#16a34a", border: "#16a34a" }; // 🟢 normal
  if (ratio <= 3)
    return { bg: "#fffbeb", text: "#b45309", border: "#f59e0b" }; // 🟡 leve
  if (ratio <= 10)
    return { bg: "#fff7ed", text: "#ea580c", border: "#ea580c" }; // 🟠 moderado
  return { bg: "#fef2f2", text: "#dc2626", border: "#dc2626" }; // 🔴 acentuado
};

// Calcula a tendência de um marcador tumoral entre coletas (B195: generalizada
// de AFP-only para qualquer marcador — afp, ca199, cea, ca125). `chave` = campo;
// `label` = rótulo usado no texto de alerta. Aceita vírgula decimal.
const calcularTendenciaMarcador = (
  marcadores: any[],
  marcadorAtualId: string,
  chave: string,
  label: string
): {
  tendencia: "subindo" | "descendo" | "estavel" | null;
  delta: number | null;
  alerta: string | null;
} => {
  if (marcadores.length < 2)
    return { tendencia: null, delta: null, alerta: null };

  const num = (m: any) => parseFloat(String(m[chave]).replace(",", "."));

  // Ordenar por data (mais antigo primeiro)
  const ordenados = [...marcadores]
    .filter((m) => m[chave] && !isNaN(num(m)))
    .sort(
      (a, b) =>
        new Date(a.dataColeta).getTime() - new Date(b.dataColeta).getTime()
    );

  if (ordenados.length < 2)
    return { tendencia: null, delta: null, alerta: null };

  // Encontrar índice do marcador atual
  const indexAtual = ordenados.findIndex((m) => m.id === marcadorAtualId);
  if (indexAtual <= 0) return { tendencia: null, delta: null, alerta: null };

  const atual = num(ordenados[indexAtual]);
  const anterior = num(ordenados[indexAtual - 1]);

  const delta = atual - anterior;
  const percentual = ((atual - anterior) / anterior) * 100;

  let tendencia: "subindo" | "descendo" | "estavel";
  let alerta: string | null = null;

  if (percentual > 10) {
    tendencia = "subindo";
    if (percentual >= 100) {
      alerta = `⚠️ ${label} DUPLICOU - Risco de progressão tumoral!`;
    } else if (percentual >= 50) {
      alerta = `⚠️ ${label} subindo rapidamente (+50%)`;
    }
  } else if (percentual < -10) {
    tendencia = "descendo";
  } else {
    tendencia = "estavel";
  }

  return { tendencia, delta, alerta };
};

// Ícone de tendência
const getIconeTendencia = (
  tendencia: "subindo" | "descendo" | "estavel" | null
): string => {
  switch (tendencia) {
    case "subindo":
      return "↑";
    case "descendo":
      return "↓";
    case "estavel":
      return "→";
    default:
      return "";
  }
};

// Cor do ícone de tendência
const getCorTendencia = (
  tendencia: "subindo" | "descendo" | "estavel" | null
): string => {
  switch (tendencia) {
    case "subindo":
      return "#dc2626"; // Vermelho
    case "descendo":
      return "#16a34a"; // Verde
    case "estavel":
      return "#6b7280"; // Cinza
    default:
      return "inherit";
  }
};

// ========================================
// 🎨 COR GENÉRICA POR FAIXA DE REFERÊNCIA (B195 Fase 2)
// ========================================
// min/max = faixa de referência confirmada por Leonardo (CRM-RO 3793, AI_91).
// ladoAlerta = lado fora da faixa clinicamente preocupante (fica VERMELHO); o
// outro lado fica AZUL (fora da faixa, mas informativo). Marcadores SEM config
// (ex.: autoanticorpos textuais "Negativo") não recebem cor. A AFP NÃO usa esta
// config — tem getCorAFP próprio (tiers de CHC).
const CONFIG_COR_MARCADOR: Record<
  string,
  { min: number; max: number; ladoAlerta: "alto" | "baixo" | "ambos" }
> = {
  cobreSerico: { min: 70, max: 140, ladoAlerta: "ambos" },
  cobreUrinario: { min: 0, max: 60, ladoAlerta: "alto" },
  ceruloplasmina: { min: 20, max: 35, ladoAlerta: "baixo" },
  alfa1Antitripsina: { min: 90, max: 200, ladoAlerta: "baixo" },
  // Função renal / urina 24h (B279 — AI_120)
  clearanceCreatinina: { min: 90, max: 160, ladoAlerta: "baixo" },
  clearanceCreatininaCorrigido: { min: 90, max: 140, ladoAlerta: "baixo" },
  proteinuria24h: { min: 0, max: 150, ladoAlerta: "alto" },
  sodioUrinarioAmostra: { min: 20, max: 250, ladoAlerta: "baixo" },
  sodioUrinario24h: { min: 40, max: 220, ladoAlerta: "ambos" },
  iggSerico: { min: 700, max: 1600, ladoAlerta: "alto" },
  igg4: { min: 0, max: 135, ladoAlerta: "alto" },
  efpAlbumina: { min: 3.5, max: 5.0, ladoAlerta: "baixo" },
  efpAlfa1: { min: 0.1, max: 0.4, ladoAlerta: "ambos" },
  efpAlfa2: { min: 0.5, max: 1.0, ladoAlerta: "ambos" },
  efpBeta: { min: 0.6, max: 1.2, ladoAlerta: "ambos" },
  efpGama: { min: 0.7, max: 1.6, ladoAlerta: "alto" },
};

const getCorMarcador = (
  chave: string,
  valor: string | undefined
): { bg: string; text: string; border: string } => {
  const transparente = {
    bg: "transparent",
    text: "inherit",
    border: "transparent",
  };
  const cfg = CONFIG_COR_MARCADOR[chave];
  if (!cfg || valor === undefined || valor === null || valor === "")
    return transparente;
  // Aceita vírgula decimal (ex.: "3,5")
  const num = parseFloat(String(valor).replace(",", "."));
  if (isNaN(num)) return transparente;

  // 🟢 Dentro da faixa → normal
  if (num >= cfg.min && num <= cfg.max)
    return { bg: "#f0fdf4", text: "#16a34a", border: "#16a34a" };

  // Fora da faixa: o lado é "baixo" (< min) ou "alto" (> max)
  const lado = num < cfg.min ? "baixo" : "alto";
  const ehAlerta = cfg.ladoAlerta === "ambos" || cfg.ladoAlerta === lado;
  // 🔴 lado clinicamente preocupante | 🔵 outro lado (informativo)
  return ehAlerta
    ? { bg: "#fef2f2", text: "#dc2626", border: "#dc2626" }
    : { bg: "#eff6ff", text: "#2563eb", border: "#2563eb" };
};

interface TabelaMarcadoresProps {
  paciente: Paciente;
}

export default function TabelaMarcadores({ paciente }: TabelaMarcadoresProps) {
  const {
    listarMarcadoresPorPaciente,
    adicionarMarcador,
    editarMarcador,
    removerMarcador,
  } = useMarcadores();

  const marcadores = listarMarcadoresPorPaciente(paciente.id);
  // NB: o componente já usa `exames` local (config dos campos) — evitamos colisão.
  const { exames: examesLab } = useExames();
  const { atualizarPaciente } = usePacientes();
  const { usuarioLogado } = useAuth();

  // 🧂 B312/AI_141 — sódio SÉRICO mais recente do paciente, para a análise de IA
  // correlacionar com o sódio urinário (natriurese) na interpretação renal.
  const sodioSericoRecente = React.useMemo(() => {
    const comSodio = examesLab
      .filter(
        (e: any) =>
          e.pacienteId === paciente.id &&
          e.sodio !== undefined &&
          e.sodio !== null &&
          e.sodio !== "" &&
          !isNaN(Number(e.sodio))
      )
      .sort(
        (a: any, b: any) =>
          new Date(b.data).getTime() - new Date(a.data).getTime()
      );
    return comSodio[0]
      ? { valor: Number(comSodio[0].sodio), data: comSodio[0].data }
      : null;
  }, [examesLab, paciente.id]);
  // 🔐 B88 (AI_98): nome legado - hoje significa "somente leitura".
  // Cobre convidado E observador (espelho de canWriteClinical das rules).
  const isConvidado = !podeEscreverClinico(usuarioLogado);
  // 🔐 B323 (AI_145): admin mantém editar/excluir após 24h (escape B296).
  const ehAdmin = ehAdministrador(usuarioLogado);
  // §6.3.2/B88 (AI_145): GERAÇÃO de IA é ato médico (só o botão "Análise IA");
  // "Ver última análise" (leitura) permanece à equipe sob !isConvidado.
  const podeGerarIA = ehMedico(usuarioLogado);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [marcadorEditando, setMarcadorEditando] = useState<string | null>(null);
  const [dadosEdicao, setDadosEdicao] = useState<any>({});

  // ========================================
  // 🤖 ESTADOS PARA ANÁLISE IA
  // ========================================
  const [mostrarAnaliseIA, setMostrarAnaliseIA] = useState(false);
  const [loadingIA, setLoadingIA] = useState(false);
  const [resultadoIA, setResultadoIA] = useState<string | null>(null);
  const [erroIA, setErroIA] = useState<string | null>(null);
  // B312-FU/AI_141 — data (ISO) da análise atualmente exibida (recém-gerada ou a
  // "última" reaberta), para o subtítulo do modal.
  const [dataAnaliseExibida, setDataAnaliseExibida] = useState<string | null>(
    null
  );
  // B312-FU/AI_141 — cópia local da última análise gerada nesta sessão, para o
  // botão "Ver última análise" aparecer NA HORA (antes do snapshot do Firestore
  // voltar) e mesmo se a gravação durável falhar (soft-fail).
  const [ultimaAnaliseLocal, setUltimaAnaliseLocal] = useState<{
    texto: string;
    geradaEm: string;
  } | null>(null);
  // Fonte única do botão: o que veio persistido no paciente OU a cópia local.
  const ultimaAnaliseDisponivel =
    paciente.ultimaAnaliseMarcadores || ultimaAnaliseLocal;

  // ========================================
  // 🤖 FUNÇÃO DE ANÁLISE IA
  // ========================================
  const handleAnaliseIA = async () => {
    if (marcadores.length === 0) {
      alert("Adicione pelo menos uma coleta de marcadores para análise.");
      return;
    }

    const prompt = gerarPromptMarcadores(
      paciente,
      marcadores,
      sodioSericoRecente
    );
    setMostrarAnaliseIA(true);
    setLoadingIA(true);
    setErroIA(null);
    setResultadoIA(null);
    setDataAnaliseExibida(null);

    try {
      // B120 (AI_66) — Migrado para analyzeClient (auth Firebase ID Token)
      // Try/catch externo (linhas acima/abaixo) captura erros e popula
      // setErroIA — comportamento UX preservado bit-a-bit
      const data = await callAnalyze<{
        content?: Array<{ type: string; text: string }>;
      }>({
        prompt: prompt,
        useThinking: true,
      });
      const textoResposta = data.content?.[0]?.text || "Sem resposta da IA";
      setResultadoIA(textoResposta);
      const agora = new Date().toISOString();
      setDataAnaliseExibida(agora);
      setUltimaAnaliseLocal({ texto: textoResposta, geradaEm: agora });

      // 💾 B312-FU/AI_141 — persiste a última análise no paciente (durável), para
      // reabrir depois (botão "Ver última análise"). Soft-fail: se a gravação
      // falhar, a análise segue exibida normalmente. atualizarPaciente só registra
      // o NOME do campo na trilha (não o texto) — sem poluir a auditoria.
      try {
        await atualizarPaciente(paciente.id, {
          ultimaAnaliseMarcadores: {
            texto: textoResposta,
            geradaEm: agora,
            geradaPor: usuarioLogado?.id || "",
            geradaPorNome: usuarioLogado?.nome || "",
          },
        });
      } catch (e) {
        console.warn(
          "⚠️ Não foi possível salvar a última análise de marcadores:",
          e
        );
      }
    } catch (error: any) {
      console.error("Erro na análise IA:", error);
      setErroIA(error.message || "Erro ao gerar análise");
    } finally {
      setLoadingIA(false);
    }
  };

  // 👁️ B312-FU/AI_141 — reabre a última análise salva (sem gerar de novo / sem
  // custo de IA). Lê do paciente (persistido no Firestore).
  const verUltimaAnalise = () => {
    const ua = ultimaAnaliseDisponivel;
    if (!ua?.texto) return;
    setErroIA(null);
    setLoadingIA(false);
    setResultadoIA(ua.texto);
    setDataAnaliseExibida(ua.geradaEm || null);
    setMostrarAnaliseIA(true);
  };

  const fecharAnaliseIA = () => {
    setMostrarAnaliseIA(false);
    setResultadoIA(null);
    setErroIA(null);
    setDataAnaliseExibida(null);
  };
  const [novaColeta, setNovaColeta] = useState({
    dataColeta: "",
    afp: "",
    ca199: "",
    cea: "",
    ca125: "",
    psa: "",
    ferro: "",
    ferritina: "",
    saturacaoTransferrina: "",
    cobreSerico: "",
    cobreUrinario: "",
    ceruloplasmina: "",
    alfa1Antitripsina: "",
    clearanceCreatinina: "",
    clearanceCreatininaCorrigido: "",
    proteinuria24h: "",
    sodioUrinarioAmostra: "",
    sodioUrinario24h: "",
    iggSerico: "",
    igg4: "",
    efpAlbumina: "",
    efpAlfa1: "",
    efpAlfa2: "",
    efpBeta: "",
    efpGama: "",
    fan: "",
    antiLKM: "",
    antiMusculoLiso: "",
    antiMitocondria: "",
    anca: "",
    antiDNA: "",
    antiTransglutaminase: "",
  });

  const exames = [
    { key: "afp", label: "AFP", ref: "< 10 ng/mL", unidade: "ng/mL" },
    { key: "ca199", label: "CA 19-9", ref: "< 37 U/mL", unidade: "U/mL" },
    { key: "cea", label: "CEA", ref: "< 5 ng/mL", unidade: "ng/mL" },
    { key: "ca125", label: "CA 125", ref: "< 35 U/mL", unidade: "U/mL" },
    { key: "psa", label: "PSA", ref: "< 4 ng/mL", unidade: "ng/mL" },
    // Metabolismo do ferro (B241 fase 2 — AI_109)
    { key: "ferro", label: "Ferro sérico", ref: "65–175 µg/dL", unidade: "µg/dL" },
    { key: "ferritina", label: "Ferritina", ref: "30–300 ng/mL", unidade: "ng/mL" },
    { key: "saturacaoTransferrina", label: "Sat. transferrina", ref: "20–50 %", unidade: "%" },
    // Metabólicos / Etiológicos (B195)
    {
      key: "cobreSerico",
      label: "Cobre Sérico",
      ref: "70–140 µg/dL",
      unidade: "µg/dL",
    },
    {
      key: "cobreUrinario",
      label: "Cobre Urinário (24h)",
      ref: "< 60 µg/24h",
      unidade: "µg/24h",
    },
    {
      key: "ceruloplasmina",
      label: "Ceruloplasmina",
      ref: "20–35 mg/dL",
      unidade: "mg/dL",
    },
    {
      key: "alfa1Antitripsina",
      label: "Alfa-1 Antitripsina",
      ref: "90–200 mg/dL",
      unidade: "mg/dL",
    },
    // Função renal / urina 24h (B279 — AI_120)
    {
      key: "clearanceCreatinina",
      label: "Clearance creatinina (total)",
      ref: "~90–120 mL/min",
      unidade: "mL/min",
    },
    {
      key: "clearanceCreatininaCorrigido",
      label: "Clearance creatinina (corr. 1,73m²)",
      ref: "≥ 90 mL/min/1,73m²",
      unidade: "mL/min/1,73m²",
    },
    {
      key: "proteinuria24h",
      label: "Proteinúria 24h",
      ref: "< 150 mg/24h",
      unidade: "mg/24h",
    },
    {
      key: "sodioUrinarioAmostra",
      label: "Sódio urinário (amostra)",
      ref: "amostra isolada",
      unidade: "mEq/L",
    },
    {
      key: "sodioUrinario24h",
      label: "Sódio urinário (24h)",
      ref: "> 78 = natriurese adequada",
      unidade: "mEq/24h",
    },
    // Imunoglobulinas / Autoimune-IgG4 (B195)
    {
      key: "iggSerico",
      label: "IgG Sérico",
      ref: "700–1600 mg/dL",
      unidade: "mg/dL",
    },
    { key: "igg4", label: "IgG4", ref: "< 135 mg/dL", unidade: "mg/dL" },
    // Eletroforese de proteínas — frações em g/dL (B195)
    {
      key: "efpAlbumina",
      label: "Albumina (EFP)",
      ref: "3,5–5,0 g/dL",
      unidade: "g/dL",
    },
    {
      key: "efpAlfa1",
      label: "Globulina Alfa-1 (EFP)",
      ref: "0,1–0,4 g/dL",
      unidade: "g/dL",
    },
    {
      key: "efpAlfa2",
      label: "Globulina Alfa-2 (EFP)",
      ref: "0,5–1,0 g/dL",
      unidade: "g/dL",
    },
    {
      key: "efpBeta",
      label: "Globulina Beta (EFP)",
      ref: "0,6–1,2 g/dL",
      unidade: "g/dL",
    },
    {
      key: "efpGama",
      label: "Gamaglobulina (EFP)",
      ref: "0,7–1,6 g/dL",
      unidade: "g/dL",
    },
    { key: "fan", label: "FAN", ref: "Negativo", unidade: "" },
    { key: "antiLKM", label: "Anti-LKM", ref: "Negativo", unidade: "" },
    {
      key: "antiMusculoLiso",
      label: "Anti-Músculo Liso",
      ref: "Negativo",
      unidade: "",
    },
    {
      key: "antiMitocondria",
      label: "Anti-Mitocôndria (AMA)",
      ref: "Negativo",
      unidade: "",
    },
    { key: "anca", label: "ANCA", ref: "Negativo", unidade: "" },
    { key: "antiDNA", label: "Anti-DNA", ref: "Negativo", unidade: "" },
    {
      key: "antiTransglutaminase",
      label: "Anti-Transglutaminase",
      ref: "Negativo",
      unidade: "",
    },
  ];

  const adicionarNovaColeta = () => {
    if (!novaColeta.dataColeta) {
      alert("Por favor, informe a data da coleta!");
      return;
    }
    // 📅 B296 — data da coleta não pode ser futura (permite hoje/retroativo).
    if (ehDataFutura(novaColeta.dataColeta)) {
      alert(msgDataFutura("Data da coleta"));
      return;
    }
    // Verificar se pelo menos 1 resultado foi preenchido
    const camposMarcador = Object.entries(novaColeta).filter(
      ([key]) => key !== "dataColeta"
    );
    const temDados = camposMarcador.some(
      ([, valor]) => valor && String(valor).trim() !== ""
    );
    if (!temDados) {
      alert("Digite ao menos 1 resultado de marcador para salvar.");
      return;
    }
    // B338-FU (AI_150) — mesma guarda do salvarEdicao: texto não reconhecido é
    // descartado no gravar, e sem aviso o médico acha que salvou.
    const naoLidosNovo = camposNaoReconhecidos(novaColeta as any);
    if (naoLidosNovo.length) {
      const seguir = window.confirm(
        `Não consegui interpretar o que está escrito em: ${naoLidosNovo.join(", ")}.\n\n` +
          `Se continuar, esses campos ficarão em branco no registro.\n\nDeseja salvar assim mesmo?`
      );
      if (!seguir) return;
    }
    adicionarMarcador(
      paciente.id,
      padronizarRegistroMarcadores({
      pacienteId: paciente.id,
      dataColeta: novaColeta.dataColeta,
      afp: novaColeta.afp || undefined,
      ca199: novaColeta.ca199 || undefined,
      cea: novaColeta.cea || undefined,
      ca125: novaColeta.ca125 || undefined,
      psa: novaColeta.psa || undefined,
      ferro: novaColeta.ferro || undefined,
      ferritina: novaColeta.ferritina || undefined,
      saturacaoTransferrina: novaColeta.saturacaoTransferrina || undefined,
      cobreSerico: novaColeta.cobreSerico || undefined,
      cobreUrinario: novaColeta.cobreUrinario || undefined,
      ceruloplasmina: novaColeta.ceruloplasmina || undefined,
      alfa1Antitripsina: novaColeta.alfa1Antitripsina || undefined,
      clearanceCreatinina: novaColeta.clearanceCreatinina || undefined,
      clearanceCreatininaCorrigido:
        novaColeta.clearanceCreatininaCorrigido || undefined,
      proteinuria24h: novaColeta.proteinuria24h || undefined,
      sodioUrinarioAmostra: novaColeta.sodioUrinarioAmostra || undefined,
      sodioUrinario24h: novaColeta.sodioUrinario24h || undefined,
      iggSerico: novaColeta.iggSerico || undefined,
      igg4: novaColeta.igg4 || undefined,
      efpAlbumina: novaColeta.efpAlbumina || undefined,
      efpAlfa1: novaColeta.efpAlfa1 || undefined,
      efpAlfa2: novaColeta.efpAlfa2 || undefined,
      efpBeta: novaColeta.efpBeta || undefined,
      efpGama: novaColeta.efpGama || undefined,
      fan: novaColeta.fan || undefined,
      antiLKM: novaColeta.antiLKM || undefined,
      antiMusculoLiso: novaColeta.antiMusculoLiso || undefined,
      antiMitocondria: novaColeta.antiMitocondria || undefined,
      anca: novaColeta.anca || undefined,
      antiDNA: novaColeta.antiDNA || undefined,
      antiTransglutaminase: novaColeta.antiTransglutaminase || undefined,
      })
    );

    setNovaColeta({
      dataColeta: "",
      afp: "",
      ca199: "",
      cea: "",
      ca125: "",
      psa: "",
      ferro: "",
      ferritina: "",
      saturacaoTransferrina: "",
      cobreSerico: "",
      cobreUrinario: "",
      ceruloplasmina: "",
      alfa1Antitripsina: "",
      clearanceCreatinina: "",
      clearanceCreatininaCorrigido: "",
      proteinuria24h: "",
      sodioUrinarioAmostra: "",
      sodioUrinario24h: "",
      iggSerico: "",
      igg4: "",
      efpAlbumina: "",
      efpAlfa1: "",
      efpAlfa2: "",
      efpBeta: "",
      efpGama: "",
      fan: "",
      antiLKM: "",
      antiMusculoLiso: "",
      antiMitocondria: "",
      anca: "",
      antiDNA: "",
      antiTransglutaminase: "",
    });
    setModoEdicao(false);
  };

  // B338-FU (AI_150) — guarda o registro como estava, p/ o aviso de "não
  // reconheci" cobrar só o que o médico mudou nesta edição.
  const originalEdicaoRef = useRef<Record<string, any>>({});

  const iniciarEdicao = (marcador: any) => {
    setMarcadorEditando(marcador.id);
    setDadosEdicao({ ...marcador });
    originalEdicaoRef.current = { ...marcador };
  };

  const cancelarEdicao = () => {
    setMarcadorEditando(null);
    setDadosEdicao({});
  };

  const salvarEdicao = () => {
    if (marcadorEditando) {
      // 📅 B296 — guarda contra data futura também na edição.
      if (ehDataFutura((dadosEdicao as any).dataColeta)) {
        alert(msgDataFutura("Data da coleta"));
        return;
      }
      // B338-FU (AI_150) — texto não reconhecido some em silêncio ao gravar (vira
      // undefined e é descartado): o médico não perceberia. Pergunta antes.
      const naoLidos = camposNaoReconhecidos(
        dadosEdicao as any,
        {},
        originalEdicaoRef.current
      );
      if (naoLidos.length) {
        const seguir = window.confirm(
          `Não consegui interpretar o que está escrito em: ${naoLidos.join(", ")}.\n\n` +
            `Se continuar, esses campos NÃO serão alterados (o valor anterior permanece). ` +
            `Para apagar um campo, deixe-o em branco.\n\nDeseja salvar assim mesmo?`
        );
        if (!seguir) return;
      }
      // B247: padroniza os autoanticorpos no gravar (tumorais/numéricos intactos).
      editarMarcador(marcadorEditando, padronizarRegistroMarcadores(dadosEdicao));
      setMarcadorEditando(null);
      setDadosEdicao({});
    }
  };

  const formatarData = (dataString: string): string => {
    const [ano, mes, dia] = dataString.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "16px",
        maxHeight: "calc(100vh - 300px)",
        overflowY: "auto",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: theme.gray[800],
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: theme.fonts.sans,
            margin: 0,
          }}
        >
          <Activity size={18} style={{ marginRight: "8px" }} /> Marcadores e
          Autoanticorpos
        </h2>
        <div style={{ display: "flex", gap: "12px" }}>
          {!isConvidado && (
            <>
              {/* 🤖 BOTÃO DE ANÁLISE IA — §6.3.2: gerar IA é ato médico */}
              {podeGerarIA && (
              <button
                onClick={handleAnaliseIA}
                disabled={loadingIA || marcadores.length === 0}
                style={{
                  padding: "10px 20px",
                  background: loadingIA
                    ? theme.gray[400]
                    : "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: theme.borderRadius.lg,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor:
                    loadingIA || marcadores.length === 0
                      ? "not-allowed"
                      : "pointer",
                  fontFamily: theme.fonts.sans,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: marcadores.length === 0 ? 0.5 : 1,
                }}
              >
                {loadingIA ? (
                  <>
                    <Loader size={14} style={{ marginRight: "4px" }} />
                    Analisando...
                  </>
                ) : (
                  <>
                    <Bot size={14} style={{ marginRight: "4px" }} />
                    Análise IA
                  </>
                )}
              </button>
              )}

              {/* 👁️ B312-FU/AI_141 — reabre a ÚLTIMA análise (persistida no
                  paciente OU gerada nesta sessão). Sem gerar de novo. */}
              {ultimaAnaliseDisponivel?.texto && (
                <button
                  onClick={verUltimaAnalise}
                  disabled={loadingIA}
                  style={{
                    padding: "10px 20px",
                    background: "white",
                    color: theme.primary[700],
                    border: `1px solid ${theme.primary[300]}`,
                    borderRadius: theme.borderRadius.lg,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: loadingIA ? "not-allowed" : "pointer",
                    fontFamily: theme.fonts.sans,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    opacity: loadingIA ? 0.5 : 1,
                  }}
                  title="Reabrir a última análise gerada (não gera uma nova)"
                >
                  <Eye size={14} style={{ marginRight: "4px" }} />
                  Ver última análise
                </button>
              )}
              <Button onClick={() => setModoEdicao(!modoEdicao)}>
                {modoEdicao ? (
                  <>
                    <X size={14} style={{ marginRight: "6px" }} />
                    Cancelar
                  </>
                ) : (
                  <>
                    <Plus size={14} style={{ marginRight: "6px" }} />
                    Nova Coleta
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* FORMULÁRIO NOVA COLETA */}
      {modoEdicao && (
        <div
          style={{
            background: theme.gray[50],
            padding: "12px",
            borderRadius: theme.borderRadius.lg,
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "12px",
            }}
          >
            <label
              style={{
                fontWeight: "600",
                minWidth: "120px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Calendar size={14} style={{ marginRight: "6px" }} /> Data da
              Coleta:
            </label>
            <input
              type="date"
              value={novaColeta.dataColeta}
              max={hojeISO()}
              onChange={(e) =>
                setNovaColeta({ ...novaColeta, dataColeta: e.target.value })
              }
              style={{
                padding: "8px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
              }}
            />
            <Button onClick={adicionarNovaColeta}>
              <Save size={14} style={{ marginRight: "6px" }} /> Salvar Coleta
            </Button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: "20px",
              marginBottom: "16px",
            }}
          >
            {exames.map((exame) => (
              <div key={exame.key}>
                <label
                  style={{
                    fontSize: "12px",
                    color: theme.gray[600],
                    display: "block",
                    marginBottom: "4px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {exame.label}
                </label>
                {TIPO_AUTOANTICORPO[exame.key] ? (
                  <CampoAutoanticorpo
                    campo={exame.key}
                    valor={novaColeta[exame.key as keyof typeof novaColeta]}
                    placeholder={exame.unidade}
                    onChange={(v) =>
                      setNovaColeta({ ...novaColeta, [exame.key]: v })
                    }
                  />
                ) : (
                  <input
                    type="text"
                    value={novaColeta[exame.key as keyof typeof novaColeta]}
                    onChange={(e) =>
                      setNovaColeta({
                        ...novaColeta,
                        [exame.key]: e.target.value,
                      })
                    }
                    placeholder={exame.unidade}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `1px solid ${theme.gray[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TABELA */}
      <div
        style={{
          overflowX: "auto",
          maxHeight: "600px",
          border: `1px solid ${theme.gray[200]}`,
          borderRadius: theme.borderRadius.lg,
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
            fontFamily: theme.fonts.sans,
          }}
        >
          <thead>
            <tr style={{ background: theme.gray[100] }}>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  fontWeight: "600",
                  fontFamily: theme.fonts.sans,
                  position: "sticky",
                  left: 0,
                  background: theme.gray[100],
                  zIndex: 2,
                  borderRight: `2px solid ${theme.gray[300]}`,
                  minWidth: "200px",
                }}
              >
                Exame
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  fontWeight: "600",
                  fontFamily: theme.fonts.sans,
                  minWidth: "150px",
                  borderRight: `1px solid ${theme.gray[300]}`,
                }}
              >
                Valor de Referência
              </th>
              {marcadores.map((marcador) => (
                <th
                  key={marcador.id}
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    fontWeight: "600",
                    fontFamily: theme.fonts.sans,
                    minWidth: "120px",
                    borderLeft: `1px solid ${theme.gray[300]}`,
                  }}
                >
                  <div>
                    <div>{formatarData(marcador.dataColeta)}</div>
                    <div style={{ marginTop: "8px" }}>
                      {!isConvidado && (
                        <>
                          {marcadorEditando === marcador.id ? (
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                justifyContent: "center",
                              }}
                            >
                              <button
                                onClick={salvarEdicao}
                                style={{
                                  padding: "4px 8px",
                                  fontSize: "11px",
                                  background: theme.primary[600],
                                  color: "white",
                                  border: "none",
                                  borderRadius: theme.borderRadius.md,
                                  cursor: "pointer",
                                  fontFamily: theme.fonts.sans,
                                }}
                              >
                                <Save
                                  size={14}
                                  style={{ marginRight: "6px" }}
                                />{" "}
                                Salvar
                              </button>
                              <button
                                onClick={cancelarEdicao}
                                style={{
                                  padding: "4px 8px",
                                  fontSize: "11px",
                                  background: theme.gray[400],
                                  color: "white",
                                  border: "none",
                                  borderRadius: theme.borderRadius.md,
                                  cursor: "pointer",
                                  fontFamily: theme.fonts.sans,
                                }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            (() => {
                              const criadoEm = marcador.criadoEm;
                              const horasDecorridas = criadoEm
                                ? (new Date().getTime() -
                                    new Date(criadoEm).getTime()) /
                                  (1000 * 60 * 60)
                                : 0;
                              // B323 (AI_145, revisão): strict p/ espelhar a
                              // regra — sem criadoEm válido = travado p/ equipe
                              // (só admin), senão a UI mostraria botão que a
                              // regra nega ("permissão negada").
                              const dentroJanela =
                                ehAdmin || (!!criadoEm && horasDecorridas < 24);
                              if (!dentroJanela)
                                return (
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      color: theme.gray[400],
                                      fontStyle: "italic",
                                      fontFamily: theme.fonts.sans,
                                    }}
                                  >
                                    bloqueado (&gt;24h)
                                  </span>
                                );
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                    justifyContent: "center",
                                  }}
                                >
                                  <button
                                    onClick={() => iniciarEdicao(marcador)}
                                    style={{
                                      padding: "4px 8px",
                                      fontSize: "11px",
                                      background: theme.primary[100],
                                      color: theme.primary[700],
                                      border: `1px solid ${theme.primary[300]}`,
                                      borderRadius: theme.borderRadius.md,
                                      cursor: "pointer",
                                      fontFamily: theme.fonts.sans,
                                    }}
                                  >
                                    <Pencil size={14} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          "Remover esta coleta de marcadores?\n\nEsta ação não pode ser desfeita."
                                        )
                                      ) {
                                        removerMarcador(marcador.id);
                                      }
                                    }}
                                    style={{
                                      padding: "4px 6px",
                                      fontSize: "11px",
                                      background: "#fee2e2",
                                      color: "#dc2626",
                                      border: "1px solid #fecaca",
                                      borderRadius: theme.borderRadius.md,
                                      cursor: "pointer",
                                    }}
                                    title="Remover coleta"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              );
                            })()
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {exames.map((exame, index) => (
              <tr
                key={exame.key}
                style={{
                  background: index % 2 === 0 ? "white" : theme.gray[50],
                }}
              >
                <td
                  style={{
                    padding: "12px",
                    fontWeight: "600",
                    fontFamily: theme.fonts.sans,
                    position: "sticky",
                    left: 0,
                    background: index % 2 === 0 ? "white" : theme.gray[50],
                    zIndex: 1,
                    borderRight: `2px solid ${theme.gray[300]}`,
                  }}
                >
                  {exame.label}
                </td>
                <td
                  style={{
                    padding: "12px",
                    color: theme.gray[600],
                    fontSize: "12px",
                    fontFamily: theme.fonts.sans,
                    borderRight: `1px solid ${theme.gray[300]}`,
                  }}
                >
                  {exame.ref}
                </td>
                {marcadores.map((marcador) => {
                  const valor =
                    marcador[exame.key as keyof typeof marcador] || "";
                  const estaEditando = marcadorEditando === marcador.id;

                  // ========================================
                  // 🎨 COR E TENDÊNCIA DA CÉLULA
                  // ========================================
                  const isAFP = exame.key === "afp";
                  // Marcadores tumorais dinâmicos (AFP + CA 19-9/CEA/CA 125):
                  // recebem o tratamento da AFP (cor graduada + seta + alerta).
                  const isTumoral = MARCADORES_TUMORAIS.includes(exame.key);
                  // Cor: AFP (tiers CHC) | CA dinâmicos (múltiplos do LSR) |
                  // demais marcadores numéricos (faixa de referência).
                  const cor = isAFP
                    ? getCorAFP(valor as string)
                    : isTumoral
                    ? getCorTumoral(exame.key, valor as string)
                    : getCorMarcador(exame.key, valor as string);
                  // Tendência (seta ↑↓→ + alerta de duplicação): só tumorais.
                  const tendencia = isTumoral
                    ? calcularTendenciaMarcador(
                        marcadores,
                        marcador.id,
                        exame.key,
                        exame.label
                      )
                    : null;

                  return (
                    <td
                      key={marcador.id}
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontFamily: theme.fonts.sans,
                        borderLeft: `1px solid ${theme.gray[200]}`,
                        // ✅ Cor de fundo unificada (tumoral ou faixa genérica)
                        background: cor?.bg || "transparent",
                      }}
                    >
                      {estaEditando ? (
                        TIPO_AUTOANTICORPO[exame.key] ? (
                          <CampoAutoanticorpo
                            campo={exame.key}
                            valor={dadosEdicao[exame.key] || ""}
                            onChange={(v) =>
                              setDadosEdicao({ ...dadosEdicao, [exame.key]: v })
                            }
                          />
                        ) : (
                          <input
                            type="text"
                            value={dadosEdicao[exame.key] || ""}
                            onChange={(e) =>
                              setDadosEdicao({
                                ...dadosEdicao,
                                [exame.key]: e.target.value,
                              })
                            }
                            style={{
                              width: "90%",
                              padding: "6px",
                              border: `2px solid ${theme.primary[400]}`,
                              borderRadius: theme.borderRadius.md,
                              fontSize: "14px",
                              fontFamily: theme.fonts.sans,
                              textAlign: "center",
                            }}
                          />
                        )
                      ) : isTumoral && valor ? (
                        // ✅ RENDERIZAÇÃO ESPECIAL — marcadores tumorais
                        // dinâmicos (AFP + CA 19-9/CEA/CA 125): valor colorido +
                        // seta de tendência + alertas.
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "600",
                                color: cor?.text || "inherit",
                                fontSize: "14px",
                              }}
                            >
                              {valor}
                            </span>
                            {tendencia?.tendencia && (
                              <span
                                style={{
                                  fontWeight: "bold",
                                  color: getCorTendencia(tendencia.tendencia),
                                  fontSize: "16px",
                                }}
                                title={`Delta: ${tendencia.delta?.toFixed(1)} ${
                                  exame.unidade
                                }`}
                              >
                                {getIconeTendencia(tendencia.tendencia)}
                              </span>
                            )}
                          </div>
                          {/* Alerta de AFP > 1000 (DROP-OUT) — só AFP */}
                          {isAFP && parseFloat(valor as string) >= 1000 && (
                            <span
                              style={{
                                fontSize: "10px",
                                color: "#dc2626",
                                fontWeight: "600",
                                background: "#fef2f2",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                border: "1px solid #dc2626",
                              }}
                            >
                              <AlertTriangle
                                size={10}
                                style={{ marginRight: "3px" }}
                              />{" "}
                              DROP-OUT
                            </span>
                          )}
                          {/* Alerta de duplicação/subida (AFP evita choque com
                              o badge DROP-OUT; demais tumorais sempre que houver) */}
                          {tendencia?.alerta &&
                            (!isAFP || parseFloat(valor as string) < 1000) && (
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: "#ea580c",
                                  fontWeight: "500",
                                }}
                                title={tendencia.alerta}
                              >
                                <AlertTriangle size={9} />
                              </span>
                            )}
                        </div>
                      ) : valor ? (
                        // B195 Fase 2 — valor colorido por faixa (não-tumorais).
                        // Sem config (autoanticorpos) → cor neutra, como antes.
                        <span
                          style={{
                            color: cor?.text || "inherit",
                            fontWeight:
                              cor && cor.bg !== "transparent" ? "600" : "400",
                          }}
                        >
                          {valor}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {marcadores.length === 0 && !modoEdicao && (
        <div
          style={{
            textAlign: "center",
            padding: "48px",
            color: theme.gray[500],
            fontFamily: theme.fonts.sans,
          }}
        >
          <p>Nenhuma coleta registrada ainda.</p>
          <p style={{ fontSize: "12px", marginTop: "8px" }}>
            Clique em "Nova Coleta" para adicionar.
          </p>
        </div>
      )}

      {/* ========================================
          🤖 MODAL DE ANÁLISE IA
          ======================================== */}
      {mostrarAnaliseIA && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          {/* B312-FU/AI_141 — clicar FORA não fecha mais (fechava sem querer).
              Só o botão X (ou "Fechar") no rodapé fecha o modal. */}
          <div
            style={{
              background: "white",
              borderRadius: theme.borderRadius.xl,
              width: "100%",
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
          >
            {/* Header do Modal */}
            <div
              style={{
                padding: "20px 24px",
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    fontFamily: theme.fonts.sans,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  🧬 Análise Inteligente de Marcadores
                </h2>
                {dataAnaliseExibida && (
                  <span
                    style={{
                      fontSize: "12px",
                      opacity: 0.85,
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Gerada em{" "}
                    {new Date(dataAnaliseExibida).toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
              </div>
              <button
                onClick={fecharAnaliseIA}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  color: "white",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div
              style={{
                padding: "24px",
                overflowY: "auto",
                flex: 1,
              }}
            >
              {/* Loading */}
              {loadingIA && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "48px",
                      marginBottom: "20px",
                      animation: "pulse 2s infinite",
                    }}
                  >
                    <Bot size={24} />
                  </div>
                  <p
                    style={{
                      fontSize: "16px",
                      color: theme.gray[600],
                      fontFamily: theme.fonts.sans,
                      marginBottom: "8px",
                    }}
                  >
                    Analisando marcadores tumorais...
                  </p>
                  <p
                    style={{
                      fontSize: "13px",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Verificando tendências de AFP, critérios de Milão e risco de
                    drop-out
                  </p>
                </div>
              )}

              {/* Erro */}
              {erroIA && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: theme.borderRadius.lg,
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      color: "#dc2626",
                      fontWeight: "600",
                      marginBottom: "8px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <X size={14} style={{ marginRight: "6px" }} /> Erro na
                    análise
                  </p>
                  <p
                    style={{
                      color: "#991b1b",
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {erroIA}
                  </p>
                </div>
              )}

              {/* Resultado */}
              {resultadoIA && !loadingIA && (
                // 🎨 B312/AI_141 — usa o renderizador compartilhado
                // TextoAnaliseMarkdown (mesmo da análise de imagem, B304/B305):
                // trata #/##/### títulos, negrito, listas, TABELAS markdown e
                // caixas de alerta por marcador real. Antes havia um renderizador
                // ad-hoc aqui que deixava "##" e "---" crus na tela.
                <TextoAnaliseMarkdown texto={resultadoIA} />
              )}
            </div>

            {/* Footer do Modal */}
            {resultadoIA && !loadingIA && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: `1px solid ${theme.gray[200]}`,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <button
                  onClick={fecharAnaliseIA}
                  style={{
                    padding: "10px 24px",
                    background: theme.gray[100],
                    color: theme.gray[700],
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.lg,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
